/**
 * FormContainerControl
 * Adapter that bridges ControlRendererProps → FormContainer component props.
 * Fetches child form schemas by formID and renders them via FormRenderer,
 * applying the configured data binding strategy (shared-root / sub-root / independent).
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { FormContainer } from '@atlas-forms/controls-form-container-react';
import { FormDefinitionApiClient, getAtlasApiConfig } from '@atlas-forms/client-js';
import type { DataBindingConfig } from '@atlas-forms/controls-form-container-react';
import type { FormSchema } from '@atlas-forms/types-js';
import { FormRenderer } from '../../components/FormRenderer';
import { FormThemeProvider, useFormTheme } from '../../contexts/FormThemeContext';
import type { ControlRendererProps } from '../ControlRenderer';

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Extract the slice of master data that a child form should see */
function resolveChildData(
  master: Record<string, unknown>,
  binding: DataBindingConfig
): Record<string, unknown> {
  if (binding.mode === 'sub-root') {
    const val = binding.rootPath.split('.').reduce<unknown>((obj, key) => {
      if (obj && typeof obj === 'object') return (obj as Record<string, unknown>)[key];
      return undefined;
    }, master);
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      return val as Record<string, unknown>;
    }
    // Path doesn't exist — return empty object (and optionally create the path)
    if (binding.createRootIfMissing !== false) {
      const initial = binding.rootInitialValue ?? {};
      return typeof initial === 'object' && !Array.isArray(initial)
        ? (initial as Record<string, unknown>)
        : {};
    }
    return {};
  }
  if (binding.mode === 'independent') return {};
  // shared-root: child sees the full master
  return master;
}

/**
 * Immutably set a value at a dot-separated path.
 * Each level is shallow-cloned so no part of the original tree is mutated.
 */
function setAtPath(
  base: Record<string, unknown>,
  path: string,
  value: unknown
): Record<string, unknown> {
  const [head, ...rest] = path.split('.');
  if (rest.length === 0) {
    return { ...base, [head]: value };
  }
  const nested =
    base[head] && typeof base[head] === 'object' && !Array.isArray(base[head])
      ? (base[head] as Record<string, unknown>)
      : {};
  return { ...base, [head]: setAtPath(nested, rest.join('.'), value) };
}

// ─── Component ────────────────────────────────────────────────────────────────

export const FormContainerControl: React.FC<ControlRendererProps> = ({
  control,
  value,
  onChange,
  disabled,
  mode,
}) => {
  const theme = useFormTheme();
  const config = control.config ?? {};
  const childForms: Array<{ formID: string }> = (config.childForms as Array<{ formID: string }> | undefined) ?? [];

  // Don't render if hidden
  if (control.hidden) {
    return null;
  }

  // Determine if form is editable based on control properties and mode
  const isDisabled = disabled || control.disabled || control.readonly || mode === 'view';

  const [schemas, setSchemas] = useState<Record<string, FormSchema>>({});

  // Always-current reference to master data — prevents stale closures in onSubmit
  const masterRef = useRef<Record<string, unknown>>({});
  const masterData: Record<string, unknown> =
    value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  masterRef.current = masterData;

  // Stable key: re-fetch only when the actual set of child form IDs changes
  const childFormIdsKey = childForms.map((c) => c.formID).join(',');

  useEffect(() => {
    if (!childFormIdsKey) return;

    const controller = new AbortController();
    const cfg = getAtlasApiConfig();
    const client = new FormDefinitionApiClient({ baseUrl: cfg.baseUrl });

    const uniqueIds = [...new Set(childFormIdsKey.split(',').filter(Boolean))];

    Promise.all(
      uniqueIds.map(async (formID): Promise<{ formID: string; schema: FormSchema } | null> => {
        try {
          // Support numeric IDs ("1", "10006") and string names ("company-form")
          const result = /^\d+$/.test(formID)
            ? await client.getFormById(Number(formID))
            : await client.getFormByName(formID);

          if (!result.success || !result.data) return null;

          const form = (result.data as any).form ?? result.data;
          const schemaRaw = form?.Schema ?? form?.schema;
          if (!schemaRaw) return null;

          const schema: FormSchema =
            typeof schemaRaw === 'string' ? JSON.parse(schemaRaw) : schemaRaw;
          return { formID, schema };
        } catch {
          return null; // individual fetch failure doesn't block other forms
        }
      })
    )
      .then((results) => {
        if (controller.signal.aborted) return;
        const map: Record<string, FormSchema> = {};
        for (const r of results) {
          if (r) map[r.formID] = r.schema;
        }
        setSchemas(map);
      })
      .catch(() => {
        // Non-critical — schemas simply won't render
      });

    return () => {
      controller.abort();
    };
  }, [childFormIdsKey]);


  // Memoized renderChildForm to prevent FormContainer from re-rendering unnecessarily
  const memoizedRenderChildForm = useCallback(
    (formID: string, binding: DataBindingConfig) => {
      const schema = schemas[formID];

      if (!schema) {
        return (
          <p style={{ color: '#9ca3af', fontSize: 13, margin: '16px 0' }}>
            Loading form {formID}…
          </p>
        );
      }

      return (
        <FormThemeProvider theme={theme}>
          <FormRenderer
            schema={schema}
            mode={isDisabled ? 'view' : 'edit'}
            nested
            initialValues={resolveChildData(masterRef.current, binding)}
            onValuesChange={isDisabled ? undefined : (childValues) => {
              if (!onChange) return;
              const current = masterRef.current;
              if (binding.mode === 'sub-root') {
                onChange(setAtPath(current, binding.rootPath, childValues));
              } else if (binding.mode === 'independent') {
                // independent forms never write back to master
              } else {
                // shared-root: shallow merge into master
                onChange({ ...current, ...childValues });
              }
            }}
          />
        </FormThemeProvider>
      );
    },
    [schemas, theme, isDisabled, onChange]
  );

  // In view mode, hide the container's own submit/nav buttons
  const effectiveConfig = isDisabled
    ? { ...config, showSubmitButton: false, showNextButton: false, showPreviousButton: false }
    : config;

  return (
    <FormThemeProvider theme={theme}>
      <FormContainer
        config={effectiveConfig as any}
        initialData={masterData}
        renderChildForm={memoizedRenderChildForm}
        theme={theme}
      />
    </FormThemeProvider>
  );
};

FormContainerControl.displayName = 'FormContainerControl';
